import { Injectable, inject, signal } from '@angular/core';
import { SupabaseService } from './supabase.service';

/** public.get_club_billing_notice row: what Actively wants the club's admins to see. */
export interface BillingNotice {
  active: boolean;
  message: string | null;
  warning_at: string | null;
  outstanding: number;
  due_date: string | null;
  overdue: boolean;
  suspended: boolean;
  contact_email: string | null;
  bank_name: string | null;
  iban: string | null;
}

const DISMISS_KEY = 'billing_notice_dismissed_at';

/**
 * The payment warning Actively shows to this club's ADMINS (never members):
 * a banner under the header with the amount owed, the due date and a message.
 * Loaded only when the signed-in user is an admin; the RPC refuses everyone
 * else. Dismissing hides it for the session; it comes back on the next visit
 * while something is still owed.
 */
@Injectable({ providedIn: 'root' })
export class BillingNoticeService {
  private readonly supabaseService = inject(SupabaseService);

  readonly notice = signal<BillingNotice | null>(null);
  readonly dismissed = signal<boolean>(false);

  async load(): Promise<void> {
    const clubId = this.supabaseService.clubId;
    if (!clubId) return;
    try {
      const { data, error } = await this.supabaseService.supabase.rpc(
        'get_club_billing_notice' as never,
        { p_club_id: clubId } as never
      );
      if (error) {
        this.notice.set(null);
        return;
      }
      const row = (data as unknown as BillingNotice[] | null)?.[0] ?? null;
      this.notice.set(row && row.active ? row : null);
      // a newer warning (set after the dismissal) shows again
      const dismissedAt = sessionStorage.getItem(DISMISS_KEY);
      this.dismissed.set(!!row?.warning_at && !!dismissedAt && dismissedAt >= row.warning_at);
    } catch (e) {
      console.error('billing notice failed', e);
      this.notice.set(null);
    }
  }

  dismiss(): void {
    sessionStorage.setItem(DISMISS_KEY, this.notice()?.warning_at ?? new Date().toISOString());
    this.dismissed.set(true);
  }

  /** "20.00 €" */
  amount(): string {
    return `${Number(this.notice()?.outstanding ?? 0).toFixed(2)} €`;
  }

  /** "16/09/2026" */
  dueDate(): string {
    const iso = this.notice()?.due_date;
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }
}
